/* eslint-disable react/forbid-prop-types */
import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { useMap } from 'react-leaflet';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { noop } from 'lodash';

import ClusterTooltip from './ClusterTooltip';

// This is old school
require('leaflet.markercluster');

/**
 * Generic component responsible for displaying markers and clusters and integrating these difficult
 * parts of Leaflet into React. It's not perfect, but this should take care of the vast majority of any
 * linking complexities between React -> Leaflet -> React
 */
export default function ClusterGroup(props) {
    const {
        items,
        activeItem,
        clusterRadius,
        tooltipRenderer,
        tooltipOptions,
        markerSize,
        clusterSize,
        getMarkerPosition,
        renderMarker,
        renderCluster,
        onItemClick,
    } = props;

    const map = useMap();
    const clusterGroupRef = useRef();
    const clusterMarkers = useRef([]);
    const [itemMarkers, setItemMarkers] = useState([]);
    const getMarkerPositionRef = useRef();
    const [, setSpiderified] = useState(false);
    getMarkerPositionRef.current = getMarkerPosition;

    const onItemClickRef = useRef();
    onItemClickRef.current = onItemClick || noop;

    // create the cluster group once
    if (!clusterGroupRef.current) {
        clusterGroupRef.current = L.markerClusterGroup({
            showCoverageOnHover: false,
            maxClusterRadius: clusterRadius,
            iconCreateFunction: (cluster) => {
                const icon = L.divIcon({
                    html: '',
                    className: '',
                    iconSize: [clusterSize, clusterSize],
                });

                // this is unfortunately a complete hack to keep track of what Leaflet does with our
                // markers. This happens outside of react land, but triggers a re-render, so we can catch
                // the clusters on the next render and attach to their DOM elements.
                clusterMarkers.current.push(cluster);

                return icon;
            },
        });

        // when we click on a cluster that has places in the exact same spot, we "spiderify" and show legs out to the individual sites.
        // When that happens, we need to force a re-render as sites that weren't manually rendered now need to be. See: DE44819
        clusterGroupRef.current.on('spiderfied', () => {
            setSpiderified(true);
        });
        clusterGroupRef.current.on('unspiderfied', () => {
            setSpiderified(false);
        });

        map.addLayer(clusterGroupRef.current);
    }

    useEffect(() => {
        clusterGroupRef.current.clearLayers();
        clusterMarkers.current = [];

        const markers = items.map((item) => {
            const icon = L.divIcon({
                className: '',
                html: '',
                iconSize: [markerSize, markerSize],
            });

            const position = getMarkerPositionRef.current(item);
            const marker = L.marker(position, { icon, item });

            marker.on('click', () => onItemClickRef.current(item));

            return marker;
        });

        clusterGroupRef.current.addLayers(markers);

        setItemMarkers(markers);
    }, [items, markerSize]);

    // render the marker items which aren't clustered
    const markerCmps = itemMarkers.map((marker) => {
        const el = marker.getElement();

        return !!el && createPortal(renderMarker({ item: marker.options.item, marker }), el);
    });

    // render the cluster groups
    const clusterCmps = clusterMarkers.current.map((cluster) => {
        const el = cluster.getElement();

        if (!el) {
            return null;
        }

        return createPortal(renderCluster({ clusterItems: getClusterItems(cluster), cluster }), cluster.getElement());
    });

    return (
        <>
            {markerCmps}
            {clusterCmps}

            <ClusterTooltip
                renderer={tooltipRenderer}
                options={tooltipOptions}
                activeItem={activeItem}
                itemMarkers={itemMarkers}
                clusterMarkers={clusterMarkers.current}
            />
        </>
    );
}

ClusterGroup.propTypes = {
    items: PropTypes.array.isRequired,
    activeItem: PropTypes.object,
    clusterRadius: PropTypes.number,
    tooltipRenderer: PropTypes.func.isRequired,
    tooltipOptions: PropTypes.object,
    markerSize: PropTypes.number.isRequired,
    clusterSize: PropTypes.number.isRequired,
    getMarkerPosition: PropTypes.func.isRequired,
    renderMarker: PropTypes.func.isRequired,
    renderCluster: PropTypes.func.isRequired,
    onItemClick: PropTypes.func,
};

ClusterGroup.defaultProps = {
    activeItem: null,
    clusterRadius: 27,
    tooltipOptions: null,
    onItemClick: null,
};

function getClusterItems(cluster) {
    const markers = cluster.getAllChildMarkers();
    return markers.map((marker) => marker.options.item);
}
